import {
  LitElement,
  css,
  html,
  classMap,
  styleMap,
  ref,
  createRef,
} from '@hh/global.js'

class HHDetailsDisclosure extends LitElement {
  static properties = {
    open: {
      type: Boolean,
      attribute: 'is-open',
      reflect: true,
    },
    animate: {
      type: Boolean,
      reflect: true,
    },
    preventFocusOut: {
      type: Boolean,
      attribute: 'prevent-focus-out',
      reflect: true,
    },
  }

  $container = createRef()
  $summary = createRef()
  $details = createRef()

  constructor() {
    super()

    this.animation = null
    this.animationOpts = {
      duration: 200,
      easing: 'ease-in-out',
    }
  }

  toggle(e = false, value = undefined) {
    if (e.type === 'keyup' && e.code.toUpperCase() !== 'ENTER') {
      return true
    }

    e && e.preventDefault()

    this.open = typeof value === 'undefined' ? !this.open : value

    if (!this.animate) return

    this.$container.value.style.height = `${this.$container.value.offsetHeight}px`

    window.requestAnimationFrame(() => this.toggleAnimation())
  }

  toggleAnimation() {
    const startHeight = `${this.$container.value.offsetHeight}px`
    const endHeight = `${
      this.$summary.value.offsetHeight +
      (this.open ? this.$details.value.offsetHeight : 0)
    }px`

    if (this.animation) {
      this.animation.cancel()
    }

    this.animation = this.$container.value.animate(
      {
        height: [startHeight, endHeight],
      },
      this.animationOpts
    )

    this.animation.onfinish = () => this.onAnimationFinish()
  }

  onAnimationFinish() {
    this.animation = null
    this.$container.value.style.height = ''
  }

  closeWithEscape(event) {
    if (event.code.toUpperCase() !== 'ESCAPE') return

    const openDetailsElement = event.target.closest('details[open]')
    if (!openDetailsElement) return

    this.$container.value.removeAttribute('open')
    this.$summary.value.focus()
  }

  onFocusOut() {
    if (this.preventFocusOut) return
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.toggle(false, false)
    })
  }

  onToggle(e) {
    this.toggle(e, e.target.open)
  }

  render() {
    return html` <!-- @ts-ignore no-incompatible-type-binding -->
      <details
        ?open="${this.open}"
        @focusout="${this.onFocusOut}"
        @toggle="${this.onToggle}"
        ${ref(this.$container)}
      >
        <summary
          @keyup="${this.toggle}"
          aria-expanded="${this.open}"
          aria-controls="details-content"
          role="button"
          id="summary"
          ${ref(this.$summary)}
        >
          <slot @click="${this.toggle}" name="summary" tabindex="-1"></slot>
          <slot name="icon-open"></slot>
          <slot name="icon-close"></slot>
        </summary>
        <div
          id="details-content"
          aria-labelledby="summary"
          ${ref(this.$details)}
        >
          <slot name="details" @keyup="${this.closeWithEscape}"></slot>
        </div>
      </details>`
  }

  static styles = css`
    /*
      Start fix for miscalculated height
      Due to margins in slotted content
    */

    #summary,
    #details-content {
      display: flex;
      flex-wrap: wrap;
    }

    ::slotted(*) {
      flex-basis: 100%;
    }

    /* End fix */

    details {
      overflow: hidden;
    }

    #summary {
      position: relative;
      list-style-type: none;
      cursor: pointer;
    }

    ::slotted([slot='icon-open']),
    ::slotted([slot='icon-close']) {
      content: '';
      position: absolute;
      top: 50%;
      right: 0;
      transform: translateY(-50%);
      width: 1.4rem;
      height: 1.4rem;
      background-color: transparent;
    }

    :host([is-open]) ::slotted([slot='icon-open']),
    ::slotted([slot='icon-close']) {
      display: none;
    }

    :host([is-open]) ::slotted([slot='icon-close']),
    ::slotted([slot='icon-open']) {
      display: block;
    }

    #summary::-webkit-details-marker {
      display: none;
    }
  `
}

customElements.define('hh-details-disclosure', HHDetailsDisclosure)
